import React, { useState, useEffect, useCallback, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import { createApi, getDataApi } from "../../data/axios";
import { cashVerifyGenerate, cashVerifyStatus } from "../../data/ApiEndpoints";
import { useSelector } from "react-redux";
import { selectAuth } from "../auth/auth.selector";

const CashVerificationQR = ({ cashDraftId, onVerified }) => {
  const [qrUrl, setQrUrl] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [status, setStatus] = useState("idle"); // idle, loading, pending, verified, error
  const [verificationData, setVerificationData] = useState(null);
  const [checking, setChecking] = useState(false);
  const userInfo = useSelector(selectAuth);
  const token = userInfo?.user?.token;
  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified;

  const generateToken = useCallback(async () => {
    if (!cashDraftId) return;
    setStatus("loading");

    try {
      const res = await createApi({
        url: cashVerifyGenerate,
        body: { cash_draft_id: cashDraftId },
        token,
      });

      if (res?.status) {
        const vToken = res.data.token;
        setVerificationToken(vToken);
        const baseUrl = process.env.REACT_APP_CASH_VERIFY_URL || window.location.origin;
        setQrUrl(`${baseUrl}/cash-verify/${vToken}`);
        setStatus("pending");
      } else {
        toast.error(res?.message || "Failed to generate QR code");
        setStatus("error");
      }
    } catch (err) {
      console.error("Generate token error:", err);
      toast.error("Failed to generate QR code");
      setStatus("error");
    }
  }, [cashDraftId, token]);

  // Manual check status function
  const checkVerificationStatus = useCallback(async () => {
    if (!cashDraftId) return;
    setChecking(true);

    try {
      const res = await getDataApi({
        url: `${cashVerifyStatus}?cash_draft_id=${cashDraftId}`,
        token,
      });

      if (res?.status && res.data?.verification_status === "verified") {
        setStatus("verified");
        setVerificationData(res.data);
        toast.success("Cash verified successfully!");
        if (onVerifiedRef.current) {
          onVerifiedRef.current(verificationToken, res.data);
        }
      } else {
        toast.info("Not verified yet. Please complete verification on mobile.");
      }
    } catch (err) {
      console.error("Check status error:", err);
      toast.error("Failed to check status");
    } finally {
      setChecking(false);
    }
  }, [cashDraftId, token, verificationToken]);

  // Generate token on mount
  useEffect(() => {
    generateToken();
  }, [generateToken]);

  // Poll for verification status
  useEffect(() => {
    if (status !== "pending" || !cashDraftId) return;

    const interval = setInterval(async () => {
      try {
        const res = await getDataApi({
          url: `${cashVerifyStatus}?cash_draft_id=${cashDraftId}`,
          token,
        });

        if (res?.status && res.data?.verification_status === "verified") {
          setStatus("verified");
          setVerificationData(res.data);
          clearInterval(interval);
          if (onVerifiedRef.current) {
            onVerifiedRef.current(verificationToken, res.data);
          }
        }
      } catch (err) {
        console.error("Poll status error:", err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [status, cashDraftId, token, verificationToken]);

  if (status === "loading") {
    return (
      <div style={styles.container}>
        <p>Generating QR code...</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div style={styles.container}>
        <p style={{ color: "#dc3545" }}>Failed to generate QR code</p>
        <button onClick={generateToken} style={styles.retryBtn}>
          Retry
        </button>
      </div>
    );
  }

  if (status === "verified") {
    return (
      <div style={styles.container}>
        <div style={styles.verifiedBadge}>
          <span style={styles.checkIcon}>&#10003;</span>
          <span>Cash Verified</span>
        </div>
        {verificationData?.image && (
          <img
            src={verificationData.image}
            alt="Cash"
            style={styles.thumbnail}
          />
        )}
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <p style={styles.title}>Scan QR to verify cash</p>
      <p style={styles.subtitle}>
        Open the link on mobile to capture cash photo & denominations
      </p>
      {qrUrl && (
        <div style={styles.qrWrapper}>
          <QRCodeSVG value={qrUrl} size={200} level="M" />
        </div>
      )}
      <p style={styles.waitingText}>Waiting for mobile verification...</p>
      <button
        onClick={checkVerificationStatus}
        disabled={checking}
        style={styles.refreshBtn}
      >
        {checking ? "Checking..." : "Refresh Status"}
      </button>
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    border: "1px dashed #ccc",
    borderRadius: "12px",
    marginTop: "16px",
    backgroundColor: "#fafafa",
  },
  title: {
    fontSize: "16px",
    fontWeight: "600",
    marginBottom: "4px",
    color: "#333",
  },
  subtitle: {
    fontSize: "13px",
    color: "#666",
    marginBottom: "16px",
    textAlign: "center",
  },
  qrWrapper: {
    padding: "16px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  waitingText: {
    fontSize: "12px",
    color: "#999",
    marginTop: "12px",
    fontStyle: "italic",
  },
  verifiedBadge: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 24px",
    backgroundColor: "#d4edda",
    color: "#155724",
    borderRadius: "8px",
    fontWeight: "600",
    fontSize: "16px",
  },
  checkIcon: {
    fontSize: "20px",
    fontWeight: "bold",
  },
  thumbnail: {
    marginTop: "12px",
    width: "120px",
    height: "90px",
    objectFit: "cover",
    borderRadius: "8px",
    border: "1px solid #ddd",
  },
  retryBtn: {
    marginTop: "12px",
    padding: "8px 24px",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#007bff",
    color: "#fff",
    cursor: "pointer",
    fontSize: "14px",
  },
  refreshBtn: {
    marginTop: "16px",
    padding: "8px 20px",
    border: "1px solid #6c757d",
    borderRadius: "6px",
    backgroundColor: "#fff",
    color: "#333",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
  },
};

export default CashVerificationQR;
